import cn from 'classnames'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import Markdown from 'markdown-to-jsx'
import React, { ChangeEvent, ReactElement, useId } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './PhysicalCardAcknowledgements.style'

export type PhysicalCardAcknowledgementsValue = {
  cardNetwork: boolean
  termsAndConditions: boolean
}

export type PhysicalCardAcknowledgementsProps = {
  className?: string
  value: PhysicalCardAcknowledgementsValue
  onChange: (value: PhysicalCardAcknowledgementsValue) => void
}

/**
 * The two Astrada consent checkboxes required before enrolling a card into the physical
 * transaction feed. Shared by ReviewEnrollCard (new-card enrollment) and
 * ConfirmCardDetailsModal (legacy-card missing-details confirmation) so there is exactly one
 * acknowledgements UI/copy for this consent, not two independently-maintained copies.
 */
function PhysicalCardAcknowledgements(
  props: PhysicalCardAcknowledgementsProps,
): ReactElement {
  const { className = '', value, onChange } = props

  const translate = useTranslate()

  // The aria-label names each checkbox; these tie the actual consent paragraph to it as its
  // description. Without the association a screen-reader user tabbing the form hears only
  // "Card Network Access Acknowledgement Checkbox" and never the text they are agreeing to,
  // which for a legal consent gate means they cannot knowingly complete it. Described-by rather
  // than making the paragraph the label: the copy is several sentences of legal text, which
  // makes for an unusable accessible name.
  const cardNetworkDescriptionId = useId()
  const termsAndConditionsDescriptionId = useId()

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const name = e.currentTarget.name as keyof PhysicalCardAcknowledgementsValue
    onChange({ ...value, [name]: e.currentTarget.checked })
  }

  return (
    <div
      className={cn(
        'PhysicalCardAcknowledgements flex-column gap-5',
        className,
      )}
      css={style}
    >
      <div className="flex-row gap-3 align-start">
        <Checkbox
          aria-label={translate('Card Network Access Acknowledgement Checkbox')}
          aria-describedby={cardNetworkDescriptionId}
          data-testid="cardNetworkAcknowledgeCheckbox"
          name="cardNetwork"
          checked={value.cardNetwork}
          onChange={handleChange}
        />
        <p className="legal-text" id={cardNetworkDescriptionId}>
          <Markdown options={{ disableParsingRawHTML: true }}>
            {translate(
              'By submitting your card information and checking this box, you authorize your card network to access, monitor and share transaction data (e.g., amount, time, date, and merchant name) with Astrada (our service provider) for the purposes of identifying relevant transactions and carrying out related spend management services. You also affirm that the card being monitored is used primarily for business purposes.',
            )}
          </Markdown>
        </p>
      </div>
      <div className="flex-row gap-3 align-start">
        <Checkbox
          aria-label={translate(
            'Terms and Conditions Acknowledgement Checkbox',
          )}
          aria-describedby={termsAndConditionsDescriptionId}
          data-testid="termsAndConditionsAcknowledgeCheckbox"
          name="termsAndConditions"
          checked={value.termsAndConditions}
          onChange={handleChange}
        />
        <p className="legal-text" id={termsAndConditionsDescriptionId}>
          <Markdown options={{ disableParsingRawHTML: true }}>
            {translate(
              "By submitting your card information and checking this box, you affirm your consent so that Astrada may process said data in accordance with Extend's Terms and Conditions, Extend's Privacy Policy, and Astrada's Privacy Policy. You also acknowledge and agree that relevant transaction details may be shared with involved third parties to enable spend management processes, in accordance with Astrada's Privacy Policy. You may opt out of transaction monitoring on the payment card you enter at any time by going to your account settings.",
            )}
          </Markdown>
        </p>
      </div>
    </div>
  )
}

export default PhysicalCardAcknowledgements
